import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import Upload from "src/components/upload/upload";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { derivarDecisionesPorItem } from "src/utils/propuestasV2/decisionesRenegociacionV2";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  alertBox: {
    marginBottom: theme.spacing(2),
  },
  itemRow: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  itemRowBloqueado: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  motivoField: {
    marginTop: theme.spacing(1),
  },
  commentField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const MAX_SIZE_FILE = 50 * 1024 * 1024; // 50 MB
const MAX_FILES = 1;
const MENSAJE_POR_ESTADO_FINAL = {
  ACEPTADA: "Registro guardado. El cliente aceptó todos los servicios.",
  PARCIALMENTE_ACEPTADA:
    "Registro guardado. Quedan servicios rechazados u observados.",
  RECHAZADA: "Registro guardado. El cliente rechazó la propuesta.",
  EN_RENEGOCIACION: "Registro guardado. La propuesta queda en negociación.",
};

/**
 * Construye el estado inicial de decisiones para los ítems editables: todos
 * en ACEPTADO, que es el caso más común (el cliente aceptó todo por una vía
 * externa). Los ítems ya aceptados no entran acá: no se pueden reenviar.
 */
function buildDecisionesIniciales(itemsEditables) {
  const mapa = {};
  for (const item of itemsEditables) {
    mapa[item.id] = { decision: "ACEPTADO", comentario_cliente: "" };
  }
  return mapa;
}

function getNombreItem(item) {
  return item.faseTitulo || item.nombreServicio || `Item #${item.id}`;
}

/**
 * Registro por el canal interno de lo que el cliente pidió (aceptar,
 * rechazar u observar un servicio) cuando no respondió por el portal.
 * Diferencia central con ModalAceptacionInternaV2: acá "observar" es una
 * decisión válida, y un ítem que el cliente ya aceptó en una vuelta anterior
 * no se puede volver a enviar — el backend lo rechaza con 409 — así que se
 * muestra bloqueado y queda fuera del payload.
 */
export default function ModalObservacionInternaV2({
  open,
  setOpen,
  propuesta,
  onSuccess,
  maxWidth = "md",
  fullWidth = true,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [registrarObservacionInterna] =
    propuestasApi.useRegistrarObservacionInternaV2Mutation();

  const items = useMemo(
    () => (Array.isArray(propuesta?.items) ? propuesta.items : []),
    [propuesta],
  );

  // La decisión vigente de cada ítem viaja en `item.respuesta` (detalle de la
  // propuesta) — no se deriva del hilo, que conserva en su bitácora
  // decisiones que la ronda siguiente ya borró de propuesta_item_respuesta.
  const decisionesPorItem = useMemo(
    () => derivarDecisionesPorItem(items),
    [items],
  );

  const itemsBloqueados = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) === "ACEPTADO"),
    [items, decisionesPorItem],
  );
  const itemsEditables = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) !== "ACEPTADO"),
    [items, decisionesPorItem],
  );

  const [file, setFile] = useState(null);
  const [comentarios, setComentarios] = useState("");
  const [decisiones, setDecisiones] = useState(() =>
    buildDecisionesIniciales(itemsEditables),
  );
  const [procesando, setProcesando] = useState(false);

  // Reiniciar estado al abrir/cerrar el modal.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setComentarios("");
      setDecisiones({});
      setProcesando(false);
    }
    // Solo reaccionar al cambio de open; itemsEditables se reconcilia abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // itemsEditables cambia de referencia en cada render de propuesta.items; la
  // reconciliación de abajo solo necesita reaccionar cuando cambia el
  // conjunto de ids, no la identidad del arreglo.
  const idsEditables = itemsEditables.map((item) => item.id).join(",");

  // Reconciliar las decisiones si propuesta.items cambia mientras el modal
  // está abierto (por ejemplo, un refetch en segundo plano). Conserva lo ya
  // elegido cuando el ítem sigue siendo editable.
  useEffect(() => {
    if (!open) return;
    setDecisiones((prev) => {
      const nuevo = buildDecisionesIniciales(itemsEditables);
      for (const id of Object.keys(nuevo)) {
        if (prev[id]) nuevo[id] = prev[id];
      }
      return nuevo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsEditables]);

  const handleClose = () => {
    if (!procesando) {
      setOpen(false);
    }
  };

  const handleDropFile = useCallback((acceptedFiles) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(
        Object.assign(newFile, {
          preview: URL.createObjectURL(newFile),
        }),
      );
    } else {
      setFile(null);
    }
  }, []);

  const handleDecisionChange = (itemId, nuevaDecision) => {
    setDecisiones((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        decision: nuevaDecision,
        comentario_cliente:
          nuevaDecision === "ACEPTADO"
            ? ""
            : (prev[itemId]?.comentario_cliente ?? ""),
      },
    }));
  };

  const handleComentarioChange = (itemId, valor) => {
    setDecisiones((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], comentario_cliente: valor },
    }));
  };

  // Observar sin dejar por escrito qué pidió el cliente no es una
  // observación: el vendedor no tendría qué ajustar. El backend lo rechaza
  // igual, pero el botón deshabilitado evita el viaje al servidor para
  // averiguarlo.
  const hayObservacionSinTexto = itemsEditables.some((item) => {
    const d = decisiones[item.id];
    return d?.decision === "OBSERVADO" && !d?.comentario_cliente?.trim();
  });

  const validar = () => {
    if (!file) {
      enqueueSnackbar(
        "Debes adjuntar el comprobante de la respuesta del cliente.",
        { variant: "error" },
      );
      return false;
    }
    if (itemsEditables.length === 0) {
      enqueueSnackbar("No quedan servicios pendientes de respuesta.", {
        variant: "error",
      });
      return false;
    }
    for (const item of itemsEditables) {
      const d = decisiones[item.id];
      if (!d || !d.decision) {
        enqueueSnackbar(
          `Debes elegir una decisión para: ${getNombreItem(item)}`,
          { variant: "error" },
        );
        return false;
      }
      if (d.decision === "OBSERVADO" && !d.comentario_cliente?.trim()) {
        enqueueSnackbar(
          `Deja por escrito qué pidió el cliente sobre: ${getNombreItem(item)}`,
          { variant: "error" },
        );
        return false;
      }
    }
    return true;
  };

  const handleConfirmar = async () => {
    if (!validar()) return;

    // Solo los ítems editables viajan en el payload: reenviar uno ya
    // aceptado lo rechaza el backend con 409, aunque se repita la misma
    // decisión.
    const respuestas = itemsEditables.map((item) => {
      const d = decisiones[item.id];
      const entrada = { item_id: item.id, decision: d.decision };
      if (d.comentario_cliente?.trim()) {
        entrada.comentario_cliente = d.comentario_cliente.trim();
      }
      return entrada;
    });

    const formData = new FormData();
    formData.append("respuestas", JSON.stringify(respuestas));
    if (comentarios.trim()) {
      formData.append("comentarios", comentarios.trim());
    }
    formData.append("comprobante", file);

    setProcesando(true);
    try {
      const resultado = await registrarObservacionInterna({
        id: propuesta.id,
        formData,
      }).unwrap();
      enqueueSnackbar(
        MENSAJE_POR_ESTADO_FINAL[resultado?.estadoFinal] ??
          "Registro guardado con éxito.",
        { variant: "success" },
      );
      handleClose();
      onSuccess?.();
    } catch (error) {
      if (error?.data?.serviciosActivos?.length) {
        await AlertaModalV2({
          icon: "error",
          title: "No se pudo registrar la respuesta",
          text: error.data.message,
          confirmButtonText: "Entendido",
          showCancelButton: false,
        }).fire();
      } else {
        enqueueSnackbar(
          error?.data?.message ||
            error?.message ||
            "Ocurrió un error al registrar la respuesta del cliente.",
          { variant: "error" },
        );
      }
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      onClose={handleClose}
      fullWidth={fullWidth}
    >
      <DialogTitle className={classes.dialogTitle}>
        Registrar solicitud del cliente — Propuesta #{propuesta?.id}
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" className={classes.alertBox}>
          <Typography variant="body2">
            <strong>Registro por canal interno:</strong> usa esta opción cuando
            el cliente respondió por una vía externa (correo, WhatsApp, llamada
            telefónica, etc.). Además de aceptar o rechazar, puedes dejar un
            servicio <strong>observado</strong> cuando el cliente pidió un
            ajuste: eso abre la conversación con él. Debes adjuntar un
            comprobante que respalde lo registrado.
          </Typography>
        </Alert>

        {itemsBloqueados.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Ya aceptados (no editables)
            </Typography>
            <Stack spacing={1}>
              {itemsBloqueados.map((item) => (
                <Box key={item.id} className={classes.itemRowBloqueado}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getNombreItem(item)}
                    </Typography>
                    <Chip
                      label="Aceptado"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {itemsEditables.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Decisión por ítem
            </Typography>
            <Stack spacing={1.5}>
              {itemsEditables.map((item) => {
                const d = decisiones[item.id] ?? {
                  decision: "ACEPTADO",
                  comentario_cliente: "",
                };
                const requiereTexto = d.decision !== "ACEPTADO";
                return (
                  <Box key={item.id} className={classes.itemRow}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getNombreItem(item)}
                        {item.tipoItem && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                            sx={{ ml: 1 }}
                          >
                            ({item.tipoItem})
                          </Typography>
                        )}
                      </Typography>
                      <RadioGroup
                        row
                        value={d.decision}
                        onChange={(e) =>
                          handleDecisionChange(item.id, e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="ACEPTADO"
                          control={<Radio size="small" color="success" />}
                          label={
                            <Typography variant="body2">Aceptado</Typography>
                          }
                          disabled={procesando}
                        />
                        <FormControlLabel
                          value="OBSERVADO"
                          control={<Radio size="small" color="warning" />}
                          label={
                            <Typography variant="body2">Observado</Typography>
                          }
                          disabled={procesando}
                        />
                        <FormControlLabel
                          value="RECHAZADO"
                          control={<Radio size="small" color="error" />}
                          label={
                            <Typography variant="body2">Rechazado</Typography>
                          }
                          disabled={procesando}
                        />
                      </RadioGroup>
                    </Stack>
                    {requiereTexto && (
                      <TextField
                        className={classes.motivoField}
                        fullWidth
                        required={d.decision === "OBSERVADO"}
                        size="small"
                        error={
                          d.decision === "OBSERVADO" &&
                          !d.comentario_cliente?.trim()
                        }
                        label={
                          d.decision === "OBSERVADO"
                            ? "Qué pidió el cliente (obligatorio)"
                            : "Motivo del rechazo (opcional)"
                        }
                        placeholder="Describe con el detalle suficiente para preparar la contraoferta..."
                        value={d.comentario_cliente}
                        onChange={(e) =>
                          handleComentarioChange(item.id, e.target.value)
                        }
                        disabled={procesando}
                        variant="outlined"
                        inputProps={{ maxLength: 500 }}
                        helperText={`${d.comentario_cliente?.length ?? 0}/500 caracteres`}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No quedan servicios pendientes de respuesta en esta propuesta.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Comprobante de respaldo *
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Adjunta una imagen o PDF que respalde lo que registraste (captura de
            correo, WhatsApp, documento firmado, etc.).
          </Typography>
          <Upload
            disabled={procesando}
            file={file}
            loading={false}
            onDrop={handleDropFile}
            onDelete={() => setFile(null)}
            accept={{
              "application/pdf": [".pdf"],
              "image/*": [".png", ".jpg", ".jpeg"],
            }}
            maxSize={MAX_SIZE_FILE}
            maxFiles={MAX_FILES}
          />
        </Box>

        <TextField
          className={classes.commentField}
          fullWidth
          multiline
          rows={3}
          label="Comentarios adicionales (opcional)"
          placeholder="Ej: El cliente llamó el día 15/01/2025 y pidió revisar el valor del segundo servicio..."
          value={comentarios}
          onChange={(e) => setComentarios(e.target.value)}
          disabled={procesando}
          variant="outlined"
          inputProps={{ maxLength: 500 }}
          helperText={`${comentarios.length}/500 caracteres`}
        />
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={procesando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={procesando}
          disabled={
            procesando || itemsEditables.length === 0 || hayObservacionSinTexto
          }
          onClick={handleConfirmar}
        >
          Registrar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ModalObservacionInternaV2.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

ModalObservacionInternaV2.defaultProps = {
  onSuccess: undefined,
  maxWidth: "md",
  fullWidth: true,
};
